import React, { useState, useEffect } from 'react';
import GuideLayout from '../../components/layouts/GuideLayout';
import { guideAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  DollarSign, 
  Banknote, 
  CreditCard, 
  Building2, 
  Calendar, 
  Clock, 
  Users,
  Mail,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const GuideFinance = () => {
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCash, setExpandedCash] = useState({});
  const [expandedOnline, setExpandedOnline] = useState({});

  useEffect(() => {
    loadFinance();
  }, []);

  const loadFinance = async () => {
    try {
      const res = await guideAPI.getFinance();
      setFinance(res.data);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCashExpand = (companyId) => {
    setExpandedCash(prev => ({ ...prev, [companyId]: !prev[companyId] }));
  };

  const toggleOnlineExpand = (companyId) => {
    setExpandedOnline(prev => ({ ...prev, [companyId]: !prev[companyId] }));
  };

  if (loading) {
    return (
      <GuideLayout title="Finance">
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </GuideLayout>
    );
  }

  return (
    <GuideLayout title="Finance & Reports">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-emerald-100 text-sm">Total Earnings</p>
                <p className="text-2xl font-bold">€{finance?.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Banknote className="w-6 h-6 opacity-80" />
                <div>
                  <p className="text-amber-100 text-xs">Cash</p>
                  <p className="text-lg font-bold">€{finance?.cash_total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-6 h-6 opacity-80" />
                <div>
                  <p className="text-blue-100 text-xs">Online</p>
                  <p className="text-lg font-bold">€{finance?.online_total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cash Payments Section */}
      <Card className="mb-6">
        <CardHeader className="bg-amber-50 border-b border-amber-200">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <Banknote className="w-5 h-5" />
            Cash Payments - Contact Companies to Settle
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {finance?.cash_payments?.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <Banknote className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No cash payments to collect</p>
            </div>
          ) : (
            <div className="divide-y">
              {finance?.cash_payments?.map((company) => (
                <div key={company.company_id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleCashExpand(company.company_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{company.company_name}</h4>
                        <p className="text-sm text-slate-500">{company.total_tours} tour(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg text-amber-600">€{company.total_amount.toFixed(2)}</p>
                        <Badge className="bg-amber-100 text-amber-700">To Collect</Badge>
                      </div>
                      {expandedCash[company.company_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedCash[company.company_id] && (
                    <div className="mt-4 pl-13 space-y-3">
                      {/* Contact Info */}
                      {company.company_email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${company.company_email}`} className="text-indigo-600 hover:underline">
                            {company.company_email}
                          </a>
                        </div>
                      )}
                      
                      {/* Tours List */}
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {company.tours.map((tour, idx) => (
                          <div key={tour.id || idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                            <div>
                              <p className="font-medium">{tour.description || tour.location}</p>
                              <p className="text-slate-500 flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                {formatDateTime(tour.date)}
                                <Users className="w-3 h-3 ml-2" />
                                {tour.pax} pax
                              </p>
                            </div>
                            <p className="font-semibold text-amber-600">€{tour.amount?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Online Payments Section */}
      <Card>
        <CardHeader className="bg-blue-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <CreditCard className="w-5 h-5" />
            Online Payments - Settled Weekly by Platform
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Settlement Info Banner */}
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Weekly Settlement Schedule</p>
                <p>Online payments are settled every week on the 1st, 8th, 15th, 22nd, and 29th of each month.</p>
              </div>
            </div>
          </div>
          
          {finance?.online_payments?.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No online payments recorded</p>
            </div>
          ) : (
            <div className="divide-y">
              {finance?.online_payments?.map((company) => (
                <div key={company.company_id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleOnlineExpand(company.company_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{company.company_name}</h4>
                        <p className="text-sm text-slate-500">{company.total_tours} tour(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg text-blue-600">€{company.total_amount.toFixed(2)}</p>
                        <Badge className="bg-blue-100 text-blue-700">Platform Settled</Badge>
                      </div>
                      {expandedOnline[company.company_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedOnline[company.company_id] && (
                    <div className="mt-4 pl-13 space-y-3">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {company.tours.map((tour, idx) => (
                          <div key={tour.id || idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                            <div>
                              <p className="font-medium">{tour.description || tour.location}</p>
                              <p className="text-slate-500 flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                {formatDateTime(tour.date)}
                                <Users className="w-3 h-3 ml-2" />
                                {tour.pax} pax
                              </p>
                            </div>
                            <p className="font-semibold text-blue-600">€{tour.amount?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </GuideLayout>
  );
};

export default GuideFinance;
